/* eslint-disable react-hooks/exhaustive-deps */
import React, {useEffect, useMemo, useState} from "react"
import {Link} from "react-router-dom"
import {Tooltip, Button} from "antd"
import moment from "moment"
import cx from "classnames"
import {get, head, isEmpty, keyBy, size} from "lodash"
import {Icon} from "components/Icon"
import {Rsvp} from "components/Calendar/Common"
import {BaseButton} from "components/buttons"
import {CalendarEventType, CalendarRecurringType, RSVP} from "types/calendar"
import {
    extractEventFromRrule,
    getScheduleEventMethod,
    getRecurringInfo,
    checkIsTermMajorSystemEvent
} from "helpers/calendar"
import {calendarService, userServiceV3, attendanceService, eventTypeService, outcomeService} from "services"
import {getFullName, handleError, isValidUrl, toastSuccess} from "helpers"
import styles from "./EventContent.module.css"
import {useModel, useMutation, useVisible} from "hooks"
import {UserTextIcon} from "components/UserTextIcon"
import {WorkWeekEventType} from "types/attendance"
import {useQuery} from "@tanstack/react-query"
import {KlassDropdown} from "components/Select"
import {useWorkflowTriggerOutcomeItems} from "hooks/useWorkflowTriggerOutcomeItems"
import {ConfirmPopup} from "uiKit"
import {useTranslation} from "react-i18next"

export function EventPreviewContent(props) {
    const {eventInfo, isShowGoing, eventOptions, closeEventPreview, isWorkScheduleView, linkedCalendars} = props
    const model = useModel()
    const {t} = useTranslation(["calendarEvents", "common"])
    const {hasWorkflowTrigger} = useWorkflowTriggerOutcomeItems()
    const confirmPopup = useVisible()
    const [pendingOutcomeId, setPendingOutcomeId] = useState<number | null>(null)
    const userTimeFormat = model.getUserTimeFormat()
    const [inviteInfos, setInviteInfos] = useState({
        total: 0,
        yes: 0,
        waiting: 0,
        no: 0,
        maybe: 0,
        users: []
    })
    const [hostUsers, setHostUsers] = useState([])
    const [isShowParticipantDetail, setIsShowParticipantDetail] = useState(false)
    const [lesson, setLesson] = useState(null)
    const [activeOutcomeItem, setActiveOutcomeItem] = useState(null)
    const eventTypeId = eventInfo?._def?.extendedProps?.json_data?.eventTypeId

    const resendToAllMutation = useMutation(
        (objectId: number) => calendarService.resendInvite({object_id: objectId, all_users: true}),
        {
            onSuccess: () => toastSuccess("Invite resent to all participants"),
            onError: (err) => handleError(err)
        }
    )

    const resendToUserMutation = useMutation(
        ({objectId, userId}: {objectId: number; userId: number}) =>
            calendarService.resendInvite({object_id: objectId, user_ids: [userId]}),
        {
            onSuccess: () => toastSuccess("Invite resent"),
            onError: (err) => handleError(err)
        }
    )

    const {data: outcomeItems} = useQuery(
        ["outcome-items-event-type-details", eventTypeId],
        async () => {
            const eventTypeDetail = await eventTypeService.getOne(eventTypeId)
            return get(head(eventTypeDetail.outcomeGroups), "outcomes", [])
        },
        {
            enabled: !!eventTypeId,
            onSuccess: (data) => {
                const outcomeInfo = data.find(
                    (item) => item.outcomeId === eventInfo?._def?.extendedProps?.json_data?.completedOutcomeId
                )
                setActiveOutcomeItem(outcomeInfo || null)
            },
            initialData: []
        }
    )

    const lessonUrl = useMemo(() => {
        if (isEmpty(lesson)) {
            return ""
        }
        const {termId, courseId, scheduleId, id} = lesson
        return `/academics/instructional/term/${termId}/course/${courseId}/schedule/${scheduleId}/lessons/${id}?tab=1`
    }, [lesson])

    useEffect(() => {
        if (eventInfo) {
            getEventInfo()
            getLesson()
        }
    }, [eventInfo.id])

    if (!eventInfo) return null

    const {
        startDateTime,
        endDateTime,
        description,
        ownerFullName,
        willGo,
        reminders,
        json_data,
        isHoliday,
        isLesson,
        type,
        start_at_timezone,
        appointmentSlot
    } = eventInfo?._def?.extendedProps || {}
    const isTermMajorSystemEvent = checkIsTermMajorSystemEvent(type)
    const eventType = json_data?.eventType
    const isSystemEvent =
        isTermMajorSystemEvent ||
        [CalendarEventType.WorkSchedule, CalendarEventType.SchoolClosure].includes(type) ||
        json_data?.isFromIntegration
    const options = (eventOptions || []).filter(
        (eventOption) =>
            eventOption.icon !== "EDIT" &&
            eventOption.icon !== "DELETE" &&
            eventOption.icon !== "CALENDAR_INPUT" &&
            (!eventOption.canShow || eventOption.canShow(eventInfo._def))
    )
    const descriptionParsed = (description || "").replaceAll("\n", "<br />")
    const editEventInfo = eventOptions.find(
        (eventOption) => eventOption.icon === "EDIT" && (!eventOption.canShow || eventOption.canShow(eventInfo._def))
    )
    const deleteEventInfo = eventOptions.find(
        (eventOption) => eventOption.icon === "DELETE" && (!eventOption.canShow || eventOption.canShow(eventInfo._def))
    )
    const goingEventInfo = eventOptions.find(
        (eventOption) =>
            eventOption.icon === "CALENDAR_INPUT" && (!eventOption.canShow || eventOption.canShow(eventInfo._def))
    )
    const hasPermissionsToDeleteOtherView = model.viewOtherUserCalendar?.id
        ? model.viewOtherUserCalendar?.hasPermissionToDelete
        : true
    const canShowEdit =
        eventType === WorkWeekEventType.Daily ? false : !isTermMajorSystemEvent && !isEmpty(editEventInfo)
    const canShowDelete =
        eventType === WorkWeekEventType.Daily
            ? false
            : !isTermMajorSystemEvent && !!deleteEventInfo && hasPermissionsToDeleteOtherView
    const hasBooked = !!appointmentSlot?.booker_user_id

    const getEventInfo = async () => {
        try {
            if (json_data?.eventType === WorkWeekEventType.Daily) {
                return
            }
            const eventId = parseInt(eventInfo.id)
            if (!eventId) return
            const {invites, owner_user_id, invited_user_ids, host_user_ids} = await calendarService.getObject(eventId)
            const userIds = [...invited_user_ids, ...host_user_ids].filter(Boolean)
            let users = []
            if (!isEmpty(userIds)) {
                const {data} = await userServiceV3.getAllAutocomplete({
                    filter: {id: userIds},
                    range: {page: 1, pageSize: userIds.length}
                })
                const invitesByUserId = keyBy(invites, "invited_user_id")
                const owner = data.find((user) => user.id === owner_user_id)
                const restInvitedUsers = data.filter(
                    (user) => user.id !== owner_user_id && invited_user_ids.includes(user.id)
                )
                users = owner ? [{...owner, isOwner: true}, ...restInvitedUsers] : restInvitedUsers
                users = users.map((user) => {
                    user.will_go = get(invitesByUserId, [user.id, "will_go"])
                    return user
                })
                const hostUsers = data.filter((user) => host_user_ids.includes(user.id))
                setHostUsers(hostUsers)
            }
            let yes = 0
            let waiting = 0
            let no = 0
            let maybe = 0
            invites.forEach(({will_go}) => {
                switch (will_go) {
                    case RSVP.Yes:
                        yes += 1
                        break
                    case RSVP.NOT_SELECTED:
                        waiting += 1
                        break
                    case RSVP.No:
                        no += 1
                        break
                    case RSVP.Maybe:
                        maybe += 1
                        break
                    default:
                        break
                }
            })
            setInviteInfos({
                total: invites.length,
                yes,
                waiting,
                no,
                maybe,
                users
            })
        } catch (error) {
            handleError(error)
        }
    }

    const getLesson = async () => {
        if (!isLesson) {
            return
        }
        try {
            const eventId = parseInt(eventInfo.id)
            const {data} = await attendanceService.getLessonByCalendarEventId({calendarEventId: eventId})
            setLesson(data)
        } catch (error) {
            handleError(error)
        }
    }

    const onClickEventOption = (event, action, newValue = null) => {
        closeEventPreview()
        if (action) {
            action(event, newValue)
        }
    }

    const toggleShowParticipant = () => {
        setIsShowParticipantDetail(!isShowParticipantDetail)
    }

    const updateObjectOutcome = async (outcomeId: number) => {
        try {
            setActiveOutcomeItem(outcomeItems.find((item) => item.outcomeId === outcomeId))
            await calendarService.updateObjectOutcome({object_id: +eventInfo.id, outcome_id: outcomeId})
            closeEventPreview()
        } catch (error) {
            handleError(error)
        }
    }

    const handleOutcomeChange = (newValue) => {
        const outcomeId = newValue?.outcomeId
        if (outcomeId && hasWorkflowTrigger(outcomeId)) {
            setPendingOutcomeId(outcomeId)
            confirmPopup.open()
        } else {
            updateObjectOutcome(outcomeId)
        }
    }

    const handleConfirmOutcomeChange = () => {
        if (pendingOutcomeId) {
            updateObjectOutcome(pendingOutcomeId)
        }
        setPendingOutcomeId(null)
        confirmPopup.close()
    }

    const handleCancelOutcomeChange = () => {
        setPendingOutcomeId(null)
        confirmPopup.close()
    }

    const handleResendToAll = (e: React.MouseEvent) => {
        e.stopPropagation()
        const objectId = parseInt(eventInfo.id, 10)
        if (!objectId) return
        resendToAllMutation.mutate(objectId)
    }

    const handleResendToUser = (e: React.MouseEvent, userId: number) => {
        e.stopPropagation()
        e.preventDefault()
        const objectId = parseInt(eventInfo.id, 10)
        if (!objectId) return
        resendToUserMutation.mutate({objectId, userId})
    }

    const renderHeader = () => {
        return (
            <div className={styles.eventPreviewHeader}>
                {options.map((eventOption, index) => (
                    <Tooltip
                        key={index}
                        title={eventOption.title}
                        placement="bottom"
                        color="rgba(97,97,97,0.902)"
                        overlayInnerStyle={{minHeight: 27}}
                        overlayClassName={styles.tooltipCard}>
                        <Button
                            className={styles.eventPreviewHeaderBtn}
                            icon={
                                <Icon
                                    icon={eventOption.icon}
                                    color="#5f6368"
                                    className={cx(styles.eventPreviewHeaderIcon, eventOption.iconClassName)}
                                />
                            }
                            onClick={() => onClickEventOption(eventInfo, eventOption.action)}
                        />
                    </Tooltip>
                ))}
                <Tooltip
                    title="Close"
                    placement="bottom"
                    color="rgba(97,97,97,0.902)"
                    overlayInnerStyle={{minHeight: 27}}
                    overlayClassName={styles.tooltipCard}>
                    <Button
                        className={styles.eventPreviewHeaderBtn}
                        icon={<Icon icon="CLOSE" color="#5f6368" className={styles.eventPreviewHeaderIcon} />}
                        onClick={closeEventPreview}
                    />
                </Tooltip>
            </div>
        )
    }
    const renderTitle = () => {
        const originalColor = get(eventInfo, "_def.ui.backgroundColor", "#1e90ff")
        const title = get(eventInfo, "_def.title", "")
        if (!title) {
            return null
        }
        const calendarName = getCalendarName()
        return (
            <div className={cx(styles.eventPreviewContentRow, styles.eventPreviewContentRow__border)}>
                <div className={styles.eventPreviewContentLeft}>
                    <div className={styles.eventPreviewType} style={{background: originalColor}}></div>
                </div>
                <div className={styles.eventPreviewInfo}>
                    <p className={styles.eventPreviewTitleEvent}>{title}</p>
                    {calendarName && <p className={styles.eventPreviewTitle}>{calendarName}</p>}
                </div>
            </div>
        )
    }

    const getCalendarName = () => {
        const calendarOptions = (linkedCalendars || []).filter(
            (calendar) => calendar.isPrimary || calendar.type === "group"
        )
        if (size(calendarOptions) <= 1) {
            return null
        }
        return calendarOptions.find((calendar) => calendar.calendarId === eventInfo?._def?.extendedProps?.calendarId)
            ?.name
    }

    const renderTimeContent = () => {
        const allDay = get(eventInfo, "allDay", false)
        const isSameDate = moment(startDateTime).isSame(moment(endDateTime), "date")
        const dateFormat = "dddd, MMMM DD YYYY"
        const dateTimeFormat = "dddd, MMMM DD YYYY, HH:mm"
        if ([CalendarEventType.StartTermDate, CalendarEventType.EndTermDate].includes(type)) {
            return <span>{moment(startDateTime).format(dateFormat)}</span>
        }
        if (isSameDate || allDay) {
            return (
                <>
                    <span>{moment(startDateTime).format(dateFormat)}</span>
                    {!allDay && (
                        <>
                            <div className={styles.eventPreviewDate__dot} />
                            <span className={styles.eventPreviewDate__time}>
                                {moment(startDateTime).format(userTimeFormat)} -{" "}
                                {moment(endDateTime).format(userTimeFormat)}
                            </span>
                        </>
                    )}
                </>
            )
        }
        return (
            <span>
                {moment(startDateTime).format(dateTimeFormat)} - {moment(endDateTime).format(dateTimeFormat)}
            </span>
        )
    }

    const renderTime = () => {
        const rrule = get(eventInfo, "_def.extendedProps.rrule")
        const {recurring} = extractEventFromRrule(rrule)
        const offset = start_at_timezone ? moment.tz(start_at_timezone).utcOffset() / 60 : 0

        return (
            <>
                {!!start_at_timezone && (
                    <div className={cx(styles.eventPreviewContentRow)}>
                        <div className={styles.eventPreviewContentLeft}>
                            <Icon className={styles.infoIcon} icon="GLOBE" />
                        </div>
                        <div className={styles.eventPreviewInfo}>
                            {moment.tz(start_at_timezone).format("z")}{" "}
                            {`UTC${offset > 0 ? `+${Math.abs(offset)}` : `-${Math.abs(offset)}`}`} ({start_at_timezone})
                        </div>
                    </div>
                )}
                <div
                    className={cx(styles.eventPreviewContentRow, {
                        [styles.eventPreviewContentRow__border]: recurring === CalendarRecurringType.Never
                    })}>
                    <div className={styles.eventPreviewContentLeft}>
                        <Icon className={styles.infoIcon} icon="TIMER" />
                    </div>
                    <div className={styles.eventPreviewInfo}>
                        <div className={styles.eventPreviewDate}>{renderTimeContent()}</div>
                    </div>
                </div>
                {recurring !== CalendarRecurringType.Never && (
                    <div className={cx(styles.eventPreviewContentRow, styles.eventPreviewContentRow__border)}>
                        <div className={styles.eventPreviewContentLeft}>
                            <Icon className={styles.infoIcon} icon="REPEAT" />
                        </div>
                        <div className={styles.eventPreviewInfo}>{getRecurringInfo(rrule)}</div>
                    </div>
                )}
            </>
        )
    }

    const renderLogo = (user) => {
        const {firstName, lastName, photo} = user
        if (photo) {
            return <img src={photo.original} alt="" className={styles.participantItem__avatar} />
        }
        const text = `${head(firstName)}${head(lastName)}`.trim() || "A"
        return <UserTextIcon text={text} className={styles.participantItem__avatar} small />
    }

    const renderInviteStatus = (will_go: RSVP) => {
        switch (will_go) {
            case RSVP.Yes:
                return (
                    <span className={styles.participantItem__inviteStatus}>
                        <Icon icon="ACTIVE" className={styles.participantItem__inviteStatusIcon} />
                    </span>
                )
            case RSVP.No:
                return (
                    <span className={styles.participantItem__inviteStatus}>
                        <Icon
                            icon="CROSS_CIRCLE_FILL"
                            color="var(--error-400-base)"
                            className={styles.participantItem__inviteStatusIcon}
                        />
                    </span>
                )
            case RSVP.Maybe:
                return (
                    <span className={styles.participantItem__inviteStatus}>
                        <Icon icon="QUESTION" className={styles.participantItem__inviteStatusIcon} />
                    </span>
                )
            case RSVP.NOT_SELECTED:
            default:
                return null
        }
    }

    const renderParticipantItem = (user) => {
        const {fullName, id, isOwner, will_go} = user

        return (
            <div key={id} className={styles.participantItem}>
                <div className={styles.participantItem__avatarWrap}>
                    {renderInviteStatus(will_go)}
                    {renderLogo(user)}
                </div>
                <a href={`/users/${id}`} target="_blank" rel="noreferrer" className={styles.participantItemInfo}>
                    <span className={styles.participantItem__name}>{fullName}</span>
                    {isOwner && <span className={styles.participantItem__owner}>Organizer</span>}
                </a>
                {!isOwner && (
                    <Tooltip
                        title="Resend invite"
                        placement="bottom"
                        color="rgba(97,97,97,0.902)"
                        overlayInnerStyle={{minHeight: 27}}
                        overlayClassName={styles.tooltipCard}>
                        <Button
                            type="link"
                            size="small"
                            shape="circle"
                            icon={<Icon icon="SEND_VIA_EMAIL" color="#1E90FF" className={styles.resendInviteIcon} />}
                            loading={resendToUserMutation.isLoading && resendToUserMutation.variables?.userId === id}
                            onClick={(e) => handleResendToUser(e, id)}
                        />
                    </Tooltip>
                )}
            </div>
        )
    }

    const renderParticipantsDetail = () => {
        if (isShowParticipantDetail) {
            return (
                <div className={styles.participantsWrap}>
                    {inviteInfos.users.map((user) => renderParticipantItem(user))}
                </div>
            )
        }
        return null
    }

    const renderHostUsers = () => {
        if (isWorkScheduleView || isSystemEvent) {
            return
        }
        const hostUsersInfo = hostUsers.map((user) => getFullName(user)).join(", ")
        if (!hostUsersInfo) {
            return null
        }
        return (
            <div className={cx(styles.eventPreviewContentRow, styles.eventPreviewContentRow__border)}>
                <div className={styles.eventPreviewContentLeft}>
                    <Icon className={styles.infoIcon} icon="PERSON_ADD" />
                </div>
                <div className={styles.eventPreviewInfo}>
                    <p className={styles.eventPreviewTitle}>{hostUsersInfo}</p>
                </div>
            </div>
        )
    }

    const getCountInviteInfo = () => {
        const {yes, no, maybe, waiting} = inviteInfos
        return [
            {label: "Yes", value: yes},
            {label: "No", value: no},
            {label: "Maybe", value: maybe},
            {label: "Awaiting", value: waiting}
        ]
            .filter((item) => item.value > 0)
            .map((item) => `${item.value} ${item.label}`)
            .join(", ")
    }

    const renderParticipant = () => {
        if (isWorkScheduleView || isSystemEvent || hasBooked || eventType === WorkWeekEventType.Daily) {
            return
        }

        return (
            <div className={cx(styles.eventPreviewContentRow, styles.eventPreviewContentRow__border)}>
                <div className={styles.eventPreviewContentLeft}>
                    <Icon className={styles.infoIcon} icon="PEOPLE" />
                </div>
                <div className={styles.inviteInfoWrap}>
                    <div className={styles.inviteInfo} onClick={toggleShowParticipant}>
                        <div className={styles.eventPreviewInfo}>
                            <div className={styles.inviteInfoHeader}>
                                <p className={styles.eventPreviewTitle}>{inviteInfos.total} Participants</p>
                                <Tooltip
                                    title="Resend invites to all"
                                    placement="bottom"
                                    color="rgba(97,97,97,0.902)"
                                    overlayInnerStyle={{minHeight: 27}}
                                    overlayClassName={styles.tooltipCard}>
                                    <Button
                                        type="link"
                                        size="small"
                                        shape="circle"
                                        icon={
                                            <Icon
                                                icon="SEND_VIA_EMAIL"
                                                color="#1E90FF"
                                                className={styles.resendInviteIcon}
                                            />
                                        }
                                        loading={resendToAllMutation.isLoading}
                                        onClick={handleResendToAll}
                                    />
                                </Tooltip>
                            </div>
                            <p className={styles.participantDesc}>{getCountInviteInfo()}</p>
                        </div>
                        <div>
                            <div
                                className={cx(styles.collapseInviteIconWrap, {
                                    [styles.iconCollapse]: !isShowParticipantDetail
                                })}>
                                <Icon icon="CHEVRON_DOWN" color="#666" />
                            </div>
                        </div>
                    </div>
                    {renderParticipantsDetail()}
                </div>
            </div>
        )
    }

    const renderCourseInfo = () => {
        const {schedule_suffix, moduleTitle, subModuleTitle} = eventInfo.extendedProps || {}
        if (schedule_suffix || moduleTitle || subModuleTitle) {
            const eventMethod = getScheduleEventMethod(json_data?.eventMethod)
            return (
                <div className={cx(styles.eventPreviewContentRow, styles.eventPreviewContentRow__border)}>
                    <div className={styles.eventPreviewContentLeft}>
                        <Icon className={styles.infoIcon} icon="COURSE" />
                    </div>
                    <div className={styles.eventPreviewInfo}>
                        <p className={styles.eventPreviewTitle}>{schedule_suffix}</p>
                        <p className={styles.eventPreviewTitle}>{moduleTitle}</p>
                        <p className={styles.eventPreviewTitle}>{subModuleTitle}</p>
                        <p className={styles.eventPreviewTitle}>{eventMethod}</p>
                    </div>
                </div>
            )
        }
        return null
    }

    const renderSchoolClosureInfo = () => {
        if (type !== CalendarEventType.SchoolClosure) {
            return null
        }
        const {campuses, programs, shifts} = json_data || {}
        if (isEmpty(campuses) && isEmpty(programs) && isEmpty(shifts)) {
            return null
        }
        return (
            <>
                {!isEmpty(campuses) && (
                    <div className={cx(styles.eventPreviewContentRow, styles.eventPreviewContentRow__border)}>
                        <div className={styles.eventPreviewContentLeft}>
                            <Icon className={styles.infoIcon} icon="LOCATION" />
                        </div>
                        <div className={styles.eventPreviewInfo}>
                            <p className={styles.eventPreviewTitle}>
                                {campuses.map((campus) => campus.name).join(", ")}
                            </p>
                        </div>
                    </div>
                )}
                {!isEmpty(programs) && (
                    <div className={cx(styles.eventPreviewContentRow, styles.eventPreviewContentRow__border)}>
                        <div className={styles.eventPreviewContentLeft}>
                            <Icon className={styles.infoIcon} icon="PROGRAM" />
                        </div>
                        <div className={styles.eventPreviewInfo}>
                            <p className={styles.eventPreviewTitle}>
                                {programs.map((program) => program.name).join(", ")}
                            </p>
                        </div>
                    </div>
                )}
                {!isEmpty(shifts) && (
                    <div className={cx(styles.eventPreviewContentRow, styles.eventPreviewContentRow__border)}>
                        <div className={styles.eventPreviewContentLeft}>
                            <Icon className={styles.infoIcon} icon="ACADEMIC" />
                        </div>
                        <div className={styles.eventPreviewInfo}>
                            <p className={styles.eventPreviewTitle}>{shifts.map((shift) => shift.name).join(", ")}</p>
                        </div>
                    </div>
                )}
            </>
        )
    }

    const renderDescription = (description) => {
        if (!description || hasBooked) {
            return null
        }
        const holidayDescription = isHoliday && json_data?.notes
        if (!isEmpty(holidayDescription)) {
            description += ` (${holidayDescription})`
        }
        return (
            <div className={cx(styles.eventPreviewContentRow, styles.eventPreviewContentRow__border)}>
                <div className={styles.eventPreviewContentLeft}>
                    <Icon className={styles.infoIcon} icon="NOTES" />
                </div>
                <div className={styles.eventPreviewInfo}>
                    <div
                        className={styles.eventPreviewTitle}
                        dangerouslySetInnerHTML={{
                            __html: description
                        }}
                    />
                </div>
            </div>
        )
    }

    const renderReminders = () => {
        if (isWorkScheduleView || isSystemEvent || isEmpty(reminders)) {
            return null
        }

        return (
            <div className={cx(styles.eventPreviewContentRow, styles.eventPreviewContentRow__border)}>
                <div className={styles.eventPreviewContentLeft}>
                    <Icon className={styles.infoIcon} icon="BELL" />
                </div>
                <div className={styles.eventPreviewInfo}>
                    <p className={styles.eventPreviewTitle}>
                        {reminders?.map((reminder) => reminder.label).join(", ")}
                    </p>
                </div>
            </div>
        )
    }

    const renderLocation = () => {
        const {location} = json_data || {}
        if (isEmpty(location) || hasBooked) {
            return null
        }
        return (
            <div className={cx(styles.eventPreviewContentRow, styles.eventPreviewContentRow__border)}>
                <div className={styles.eventPreviewContentLeft}>
                    <Icon className={styles.infoIcon} icon="LOCATION" />
                </div>
                <div className={styles.eventPreviewInfo}>
                    <p className={styles.eventPreviewTitle}>{location.name}</p>
                </div>
            </div>
        )
    }

    const renderAddress = () => {
        const {address} = json_data || {}
        if (isEmpty(address)) {
            return null
        }
        const addressText = typeof address === "string" ? address.trim() : String(address)
        const looksLikeUrl = isValidUrl(addressText)
        const hasScheme = /^[a-zA-Z][a-zA-Z\d+\-.]*:/.test(addressText)
        const href = looksLikeUrl ? (hasScheme ? addressText : `https://${addressText}`) : ""
        const isUnsafeHref = /^(javascript|data|vbscript):/i.test(href)
        const showAsLink = looksLikeUrl && href && !isUnsafeHref

        return (
            <div className={cx(styles.eventPreviewContentRow, styles.eventPreviewContentRow__border)}>
                <div className={styles.eventPreviewContentLeft}>
                    <Icon className={styles.infoIcon} icon="LOCATION" />
                </div>
                <div className={styles.eventPreviewInfo}>
                    <p className={styles.eventPreviewTitle}>
                        {showAsLink ? (
                            <a href={href} target="_blank" rel="noopener noreferrer">
                                {addressText}
                            </a>
                        ) : (
                            addressText
                        )}
                    </p>
                </div>
            </div>
        )
    }

    const renderOwnerUser = () => {
        if (!ownerFullName) {
            return null
        }
        return (
            <div className={cx(styles.eventPreviewContentRow)}>
                <div className={styles.eventPreviewContentLeft}>
                    <Icon className={styles.infoIcon} icon="CALENDAR_INPUT" />
                </div>
                <div className={styles.eventPreviewInfo}>
                    <p className={styles.eventPreviewTitle}>{ownerFullName}</p>
                </div>
            </div>
        )
    }

    const renderGoing = () => {
        if (
            (model.viewOtherUserCalendar?.id && !model.viewOtherUserCalendar?.hasPermissionToEdit) ||
            eventType === WorkWeekEventType.Daily ||
            !!json_data?.without_rsvp
        ) {
            return null
        }
        return (
            <div className={styles.goingWrap}>
                <span className={styles.goingTitle}>Going?</span>
                <Rsvp
                    value={willGo}
                    onChange={(newValue) => onClickEventOption(eventInfo, goingEventInfo.action, newValue)}
                    className={styles.goingBody}
                />
            </div>
        )
    }

    const renderOutcomeItems = () => {
        if (isEmpty(outcomeItems) || !model.isStaffOrAdmin()) {
            return null
        }
        return (
            <div className={styles.goingWrap}>
                <span className={styles.goingTitle}>Outcome?</span>
                <KlassDropdown
                    options={outcomeItems}
                    value={activeOutcomeItem}
                    onChange={handleOutcomeChange}
                    placeholder="Select"
                    valueKey="outcomeId"
                />
            </div>
        )
    }

    return (
        <>
            <div className={styles.eventPreviewWrap}>
                {props.children}
                <div className={styles.eventPreviewBody}>
                    {renderHeader()}
                    <div className={styles.evenPreviewContent}>
                        {renderTitle()}
                        {renderTime()}
                        {renderHostUsers()}
                        {renderParticipant()}
                        {renderCourseInfo()}
                        {renderSchoolClosureInfo()}
                        {renderDescription(descriptionParsed)}
                        {renderReminders()}
                        {renderLocation()}
                        {renderAddress()}
                        {renderOwnerUser()}
                    </div>
                </div>
                {!isSystemEvent && renderOutcomeItems()}
                {isShowGoing && !isSystemEvent && renderGoing()}
                {isLesson && (
                    <div className={styles.eventPreviewAction}>
                        <Link to={lessonUrl}>
                            <BaseButton title="Go Lesson Detail" variant="secondary" />
                        </Link>
                    </div>
                )}
                {(canShowEdit || canShowDelete) && (
                    <div className={styles.eventPreviewAction}>
                        {canShowEdit && (
                            <BaseButton
                                title={editEventInfo.title}
                                variant="secondary"
                                onClick={() => onClickEventOption(eventInfo, editEventInfo.action)}
                            />
                        )}
                        {canShowDelete && (
                            <BaseButton
                                title="Delete Event"
                                variant="secondary"
                                onClick={() => onClickEventOption(eventInfo, deleteEventInfo.action)}
                            />
                        )}
                    </div>
                )}
            </div>
            <ConfirmPopup
                isVisible={confirmPopup.isVisible}
                title={t("calendarEvents.workflowTriggerConfirmTitle")}
                description={t("calendarEvents.workflowTriggerConfirmDescription")}
                onClose={handleCancelOutcomeChange}
                onConfirm={handleConfirmOutcomeChange}
                zIndex={10000}
            />
        </>
    )
}
